/**
 * The app shell: session, theme, nav, tab bar, and the per-screen header.
 *
 * Two things here are load-bearing product decisions rather than layout.
 *
 * **B4 — the demo role switcher must not ship.** Production renders a "Signed in
 * as" chip and derives the role from the user's claim. The switcher appears here
 * *only* when the API reports personas, and the API reports none unless
 * `API_DEV_PERSONAS` is on. So one server flag removes both the picker and the
 * ability to name a role in a header — the client cannot re-enable it.
 *
 * **B2 — global search must not ship before the search document is
 * visibility-filtered.** `rebuild_work_order_search_document` folds every comment
 * body, including `internal_only`, into one unfiltered vector per work order, so
 * any role able to search at all could match on internal text and a snippet UI
 * would render it verbatim. The nav box is therefore scoped to titles, unit
 * numbers and vendor names, and its placeholder says so rather than promising
 * "faults, comments, assets…" and quietly not delivering it.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useInRouterContext, useLocation, useNavigate } from "react-router-dom";
import {
  canBypassSiteMaintenance,
  compactPrimaryItems,
  isNavigationPathActive,
  navCountFor,
  navCountName,
  navCountText,
  navigationGroupFor,
  siteMaintenanceView,
  visibleNavigationItems,
  type Capabilities,
  type NavCounts,
  type NavigationItem,
  type SessionResponse,
  type SessionUser,
  type UserRole,
} from "@truckbuddy/contract";
import { newIdempotencyKey, readStoredPersona, writeStoredPersona } from "./api";
import { useNavCounts, useSession, useSessionState, useSetPreviewRole } from "./queries";
import {
  DEFAULT_A11Y_OPTIONS,
  DEFAULT_THEME_PRESET_ID,
  MANAGED_PROPERTIES,
  a11yOptionsToAttribute,
  isThemePresetId,
  paletteProperties,
  presetFor,
  sanitizeA11yOptions,
  sanitizeAppearance,
  type A11yOptionKey,
  type A11yOptions,
  type Appearance,
  type AppearanceOverrides,
  type AppearanceTokenKey,
  type Scheme,
  type ThemePreset,
  type ThemePresetId,
} from "./appearance";
import { useQueryClient } from "@tanstack/react-query";
import { ApiClientError } from "./api";
import { SignIn } from "../screens/SignIn";
import { Loading, QueryError } from "../components/states";
import { AccountSummary } from "../components/AccountMenu";
import { NotificationBell } from "../components/NotificationBell";
import { NavIcon, iconForPath } from "../components/NavIcons";
import { FOCUSABLE } from "../components/Dialog";
import { BrandMark } from "../components/BrandMark";
import { AppWorkspace } from "../components/AppNavigation";
import { SignOutProvider, useSignOut } from "../components/SignOutFlow";
import { PmCreditPromptHost } from "../components/PmCreditDialog";
import {
  SiteMaintenanceAnnouncement,
  SiteMaintenanceBanner,
  SiteMaintenancePage,
} from "../components/SiteMaintenance";
import { useSiteMaintenanceStatus, useSiteMaintenanceSync } from "./site-maintenance-queries";
import type { ScopeMarker } from "@truckbuddy/contract";

/* ── theme ───────────────────────────────────────────────────────── */

/**
 * The two schemes a preset sits on, the only two ever saved under THEME_KEY, and
 * the only two ever painted. (The admin-assigned tab-leave flip that once added
 * more was removed at Eden's request on 23 October 2026.)
 */
type Theme = Scheme;

const THEME_KEY = "truckbuddy.theme";
/** Which preset the person started from (Settings -> Appearance). */
const PRESET_KEY = "truckbuddy.themePreset";
/** Their colour edits, per preset. */
const APPEARANCE_KEY = "truckbuddy.appearance";
/** Accessibility Mode's eleven options. */
const A11Y_KEY = "truckbuddy.a11yOptions";
/**
 * What the pre-paint script in index.html applies before React exists: the
 * resolved inline palette and the options attribute. Rewritten from code on
 * every change, because that script cannot import `appearance.ts`.
 */
const PAINT_SNAPSHOT_KEY = "truckbuddy.paintSnapshot";

function readStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* storage disabled — the choice just will not persist */
  }
}

function readJson(key: string): unknown {
  try {
    return JSON.parse(readStorage(key) ?? "null");
  } catch {
    return null;
  }
}

function readStoredTheme(): Theme {
  const stored = readStorage(THEME_KEY);
  if (stored === "light" || stored === "dark") return stored;
  // Respect the OS preference on first visit rather than forcing light.
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

/**
 * The stored preset -- or, for anyone who never opened Appearance, Light or Dark
 * from the stored (or OS) scheme, which is exactly what they saw before presets.
 * A preset whose scheme disagrees with THEME_KEY loses: THEME_KEY is what the
 * header button and the pre-paint script agree on, and it is the newer write.
 */
function readStoredPreset(): ThemePresetId {
  const stored = readStorage(PRESET_KEY);
  const scheme = readStoredTheme();
  return isThemePresetId(stored) && presetFor(stored).scheme === scheme ? stored : scheme;
}

interface ThemeContextValue {
  /** What is painted: the preset's scheme. */
  theme: Theme;
  /** The header button: a dark preset goes to Light, anything else goes to Dark. */
  toggle: () => void;

  /* ── Settings -> Appearance (SigmaBuddy's model; see lib/appearance.ts) ── */
  presetId: ThemePresetId;
  preset: ThemePreset;
  /** Pick a preset: its scheme, its colours and which stored edits apply move together. */
  setPreset: (id: ThemePresetId) => void;
  /** The ACTIVE preset's edits; `{}` when it is as shipped. */
  appearance: AppearanceOverrides;
  /** Set one colour, or pass null to return it to the preset's own value. */
  setAppearanceToken: (key: AppearanceTokenKey, hex: string | null) => void;
  /** Drop every edit on the ACTIVE preset. Other presets keep theirs. */
  resetAppearance: () => void;
  hasCustomAppearance: boolean;
  a11yOptions: A11yOptions;
  setA11yOption: (key: A11yOptionKey, enabled: boolean) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: "light",
  toggle: () => {},
  presetId: DEFAULT_THEME_PRESET_ID,
  preset: presetFor(DEFAULT_THEME_PRESET_ID),
  setPreset: () => {},
  appearance: {},
  setAppearanceToken: () => {},
  resetAppearance: () => {},
  hasCustomAppearance: false,
  a11yOptions: DEFAULT_A11Y_OPTIONS,
  setA11yOption: () => {},
});

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [presetId, setPresetId] = useState<ThemePresetId>(readStoredPreset);
  const [allAppearance, setAllAppearance] = useState<Appearance>(() =>
    sanitizeAppearance(readJson(APPEARANCE_KEY)),
  );
  const [a11yOptions, setA11yOptions] = useState<A11yOptions>(() =>
    sanitizeA11yOptions(readJson(A11Y_KEY)),
  );

  const preset = presetFor(presetId);
  const appearance = useMemo(() => allAppearance[presetId] ?? {}, [allAppearance, presetId]);
  const theme: Theme = preset.scheme;

  useEffect(() => {
    const root = document.documentElement;
    // Set on the root element so the token block in industry.css applies to
    // everything, including portalled dialogs and context menus.
    root.setAttribute("data-theme", theme);

    // Every managed property comes off before the preset's own go on, so a
    // colour the person just reset does not linger as an inline value.
    for (const property of MANAGED_PROPERTIES) root.style.removeProperty(property);
    const custom = paletteProperties(preset, appearance);
    for (const [property, value] of Object.entries(custom)) root.style.setProperty(property, value);

    const a11y = preset.accessibility ? a11yOptionsToAttribute(a11yOptions) : null;
    root.setAttribute("data-palette", preset.id);
    if (Object.keys(custom).length) root.setAttribute("data-palette-custom", "");
    else root.removeAttribute("data-palette-custom");
    if (a11y === null) root.removeAttribute("data-a11y-opts");
    else root.setAttribute("data-a11y-opts", a11y);

    /*
     * Keep the browser chrome on the same colour as the page (TRBUD-63).
     *
     * index.html declares two `theme-color` metas keyed on
     * `prefers-color-scheme`, which is the OS preference -- and the app's theme
     * is NOT the OS preference once anyone touches the toggle, because the
     * choice is stored here and written to `data-theme`. So an OS-light phone
     * running the app in dark mode painted a near-white status bar above a
     * `#151719` page. Stamping a media-less meta wins over both of the keyed
     * ones (last matching declaration), so this line is the whole fix; the two
     * in the document stay as the pre-hydration fallback.
     *
     * The value is read from the live token rather than repeated here, so a
     * palette change in industry.css cannot leave this behind.
     */
    const bg = getComputedStyle(root).getPropertyValue("--color-bg").trim();
    if (bg) {
      let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]:not([media])');
      if (!meta) {
        meta = document.createElement("meta");
        meta.name = "theme-color";
        document.head.appendChild(meta);
      }
      meta.content = bg;
    }
    writeStorage(THEME_KEY, preset.scheme);
    writeStorage(PRESET_KEY, preset.id);
    writeStorage(PAINT_SNAPSHOT_KEY, JSON.stringify({ palette: preset.id, custom, a11y }));
  }, [theme, preset, appearance, a11yOptions]);

  useEffect(() => {
    writeStorage(APPEARANCE_KEY, JSON.stringify(allAppearance));
  }, [allAppearance]);
  useEffect(() => {
    writeStorage(A11Y_KEY, JSON.stringify(a11yOptions));
  }, [a11yOptions]);

  const toggle = useCallback(() => {
    setPresetId((id) => (presetFor(id).scheme === "dark" ? "light" : "dark"));
  }, []);
  const setPreset = useCallback((id: ThemePresetId) => {
    setPresetId(presetFor(id).id);
  }, []);
  const setAppearanceToken = useCallback(
    (key: AppearanceTokenKey, hex: string | null) =>
      setAllAppearance((current) => {
        const forPreset: AppearanceOverrides = { ...current[presetId] };
        if (hex === null) delete forPreset[key];
        else forPreset[key] = hex.toLowerCase();
        const next: Appearance = { ...current };
        // Drop the preset entirely once its last edit is cleared, so "is this
        // customised?" is a key check and a reset round-trips to the same shape.
        if (Object.keys(forPreset).length) next[presetId] = forPreset;
        else delete next[presetId];
        return next;
      }),
    [presetId],
  );
  const resetAppearance = useCallback(
    () =>
      setAllAppearance((current) => {
        if (!current[presetId]) return current;
        const next: Appearance = { ...current };
        delete next[presetId];
        return next;
      }),
    [presetId],
  );
  const setA11yOption = useCallback(
    (key: A11yOptionKey, enabled: boolean) => setA11yOptions((o) => ({ ...o, [key]: enabled })),
    [],
  );

  const value = useMemo(
    () => ({
      theme,
      toggle,
      presetId,
      preset,
      setPreset,
      appearance,
      setAppearanceToken,
      resetAppearance,
      hasCustomAppearance: Object.keys(appearance).length > 0,
      a11yOptions,
      setA11yOption,
    }),
    [
      theme,
      toggle,
      presetId,
      preset,
      setPreset,
      appearance,
      setAppearanceToken,
      resetAppearance,
      a11yOptions,
      setA11yOption,
    ],
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => useContext(ThemeContext);

/* ── session ─────────────────────────────────────────────────────── */

interface SessionContextValue {
  user: SessionUser;
  can: Capabilities;
  role: UserRole;
  /** The roles in EFFECT — the previewed one while previewing (TRBUD-253). */
  roles: UserRole[];
  personas: SessionUser[];
  fleetTimeZone: string;
  /** The role being previewed, or null. Narrowing only; see permissions.ts. */
  previewRole: UserRole | null;
  /** Which roles this account may preview. Empty means no control is offered. */
  previewableRoles: UserRole[];
  /** The account's own roles, unchanged by a preview. */
  realRoles: UserRole[];
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function useAppSession(): SessionContextValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useAppSession used outside the session provider");
  return value;
}

export function AppShell({ children }: { children: ReactNode }) {
  // The probe first, so a visitor with no cookie never sends a request that is
  // certain to 401. `/api/session` still answers 401 without one -- the invariant
  // is unchanged; what changed is that the shell stops asking when it knows.
  const state = useSessionState();
  const probeSaysSignedIn = state.data?.authenticated !== false;
  // WAIT for the probe before asking for the session. `probeSaysSignedIn` is
  // true while the probe is still in flight (no data yet is not "false"), and
  // passing it here fired `/api/session` in the same millisecond as the probe,
  // so every signed-out visit still logged the 401 the probe exists to prevent
  // (measured 1 October 2026). A probe that FAILED still asks, which is the
  // fallback `useSessionState` documents.
  const session = useSession(state.isError || state.data?.authenticated === true);
  // Maintenance mode: the polled status, and the one listener that keeps it in
  // step with refused requests and refetches everything when a window ends.
  const maintenance = useSiteMaintenanceStatus();
  useSiteMaintenanceSync();
  // The counts on the menu (TRBUD-443): read once here, before the early
  // returns as a hook must be, and drawn by both the menu and the phone bar.
  // Not asked for while a maintenance window locks this person out.
  const navCounts = useNavCounts(
    session.data,
    session.data !== undefined &&
      siteMaintenanceView(maintenance.data ?? null, canBypassSiteMaintenance(session.data.realRoles)) === "locked",
  );
  const queryClient = useQueryClient();
  const { theme, toggle } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  // The phone shows the record search as a button that reveals the field.
  const [searchOpen, setSearchOpen] = useState(false);
  const searchField = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (searchOpen) searchField.current?.focus();
  }, [searchOpen]);

  const onPersonaChange = useCallback(
    (key: string) => {
      writeStoredPersona(key);
      // A full reload rather than a cache invalidation: the persona changes the
      // role, which changes which routes exist and which default screen applies.
      // Re-deriving all of that in place is a lot of machinery for a dev-only
      // control that will not ship.
      window.location.assign("/");
    },
    [],
  );

  if (state.isLoading) return <Loading label="Signing in…" />;

  /*
   * ── Maintenance mode ────────────────────────────────────────────────
   *
   * While a window is active, everyone who cannot work through it gets the
   * maintenance page, in place, at the address they are on. Three ways in: a
   * signed-out visitor; a session the API refused with a 503 (the window
   * started since the session loaded); and a loaded session whose own roles
   * cannot bypass, the moment the poll says the window is active. An admin
   * gets the app, with the banner below saying everyone else is locked out.
   */
  const maintenanceStatus = maintenance.data ?? null;
  const refused =
    session.error instanceof ApiClientError && session.error.isSiteMaintenance
      ? session.error
      : null;
  const lockedPage = (signedIn: boolean) => (
    <SiteMaintenancePage
      status={refused?.maintenance ?? maintenanceStatus}
      signedIn={signedIn}
      checking={maintenance.isFetching || session.isFetching}
      onCheckAgain={() => {
        void maintenance.refetch();
        if (refused) void session.refetch();
      }}
      onSignedIn={() => void queryClient.invalidateQueries()}
    />
  );
  // A signed-out visitor waits for the status rather than flashing the sign-in
  // form and then swapping it for the maintenance page.
  if (!probeSaysSignedIn && maintenance.isPending && maintenance.fetchStatus === "fetching") {
    return <Loading label="Signing in…" />;
  }
  if (!probeSaysSignedIn && maintenanceStatus?.state === "active") return lockedPage(false);

  // The probe answered "no session", so go straight to the form. The 401 path
  // below still exists and still works: a session that expires between the probe
  // and the fetch lands there, as does a probe that failed and assumed a session.
  if (!probeSaysSignedIn) {
    return <SignIn onSignedIn={() => void queryClient.invalidateQueries()} />;
  }
  if (session.isLoading) return <Loading label="Signing in…" />;
  if (refused) return lockedPage(true);

  // 401 is not an error to report, it is the unauthenticated state -- the normal
  // condition for anyone who has not signed in yet. Rendering QueryError here
  // showed "Email or password is incorrect" as a fault before the person had
  // been offered anywhere to type one.
  const unauthenticated =
    session.error instanceof ApiClientError && session.error.status === 401;
  if (unauthenticated || (!session.isLoading && !session.data && !session.error)) {
    return <SignIn onSignedIn={() => void queryClient.invalidateQueries()} />;
  }
  if (session.error) {
    return (
      <div className="screen-body" style={{ paddingTop: "var(--space-6)" }}>
        <QueryError error={session.error} what="your session" />
      </div>
    );
  }
  if (!session.data) return <SignIn onSignedIn={() => void queryClient.invalidateQueries()} />;
  // The REAL roles: a preview narrows what you may do, not whether you get in.
  if (
    siteMaintenanceView(maintenanceStatus, canBypassSiteMaintenance(session.data.realRoles)) ===
    "locked"
  ) {
    return lockedPage(true);
  }

  /*
   * A live session that still owes a password change gets the change form and
   * nothing else.
   *
   * SessionResponse has carried `mustChangePassword` since it was defined —
   * "Until changed, every protected route except account/logout is refused" —
   * and nothing read it. The requirement therefore lasted one page load: the
   * flag was returned by /auth/login and acted on there, so a refresh dropped
   * an account with an admin-issued temporary password into the full app.
   *
   * Observed rather than reasoned: an account created through Settings ->
   * Users, with must_change_password true in the database, browsed Parts after a
   * reload with a complete capability set.
   *
   * This is the CHROME half. The API still serves those routes, so a caller with
   * the cookie and no browser is unaffected — closing that needs a preHandler
   * refusal with an exempt set, which is a separate decision.
   */
  if (session.data.mustChangePassword) {
    return (
      <SignIn forcePasswordChange onSignedIn={() => void queryClient.invalidateQueries()} />
    );
  }

  const {
    user,
    roles,
    can,
    personas,
    fleetTimeZone,
    previewRole,
    previewableRoles,
    realRoles,
  }: SessionResponse = session.data;
  const storedPersona = readStoredPersona();
  // The phone's More sheet lists phone-capable pages only (design v1 (a)): an
  // entry that opens "needs a larger screen" is a dead end, so Vendors, Repair
  // overview and Configuration are not offered there at all.
  const visibleTabs = visibleNavigationItems(roles, can, "phone");
  const compactTabs = compactPrimaryItems(roles, can);
  // Desktop/tablet use grouped navigation; phones retain their role-prioritized
  // compact bar. Both derive allowed destinations from the same contract.

  return (
    <SessionContext.Provider
      value={{
        user,
        roles,
        can,
        role: user.role,
        personas,
        fleetTimeZone,
        previewRole,
        previewableRoles,
        realRoles,
      }}
    >
      <SignOutProvider key={user.userId}>
      <PreviewBanner previewRole={previewRole} />
      <SiteMaintenanceBanner
        status={maintenanceStatus}
        receivedAt={maintenance.dataUpdatedAt}
        canManage={can.manageSiteMaintenance}
      />
      <SiteMaintenanceAnnouncement status={maintenanceStatus} receivedAt={maintenance.dataUpdatedAt} />
      {/*
        The top bar (TRBUD-439, design v6 frame A): the brand, the record
        search, the bell and the theme button. The account menu moved to the
        menu's foot and the head of the phone More sheet (design v1 (a)).
        Sticky on a phone only, as before: above 699px the screens' own sticky
        panes are placed for a header that scrolls away.
      */}
      <header className={`app-topbar${searchOpen ? " app-topbar-search-open" : ""}`}>
        <Link className="app-topbar-brand" to={user.defaultRoute}>
          <BrandMark />
        </Link>

        <form className="app-topbar-search" id="app-record-search"
          onSubmit={(event) => {
            event.preventDefault();
            navigate(`/work-orders?q=${encodeURIComponent(search)}`);
          }}
        >
          <NavIcon name="search" />
          <input
            ref={searchField}
            maxLength={200}
            className="input"
            // Scoped placeholder. B2: comment bodies are deliberately NOT searched.
            // A plain text box, not `type="search"`: the queue's own box is the
            // one `searchbox` on that page (Queue.test.tsx counts it).
            placeholder="Search work orders, units, vendors"
            aria-label="Search work orders, units and vendors"
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
          />
        </form>

        <div className="app-topbar-actions">
        {personas.length > 0 ? (
          <div className="app-topbar-persona">
            <span>View as</span>
            <select
              className="input"
              aria-label="Development persona"
              value={storedPersona ?? ""}
              onChange={(event) => onPersonaChange(event.currentTarget.value)}
            >
              {storedPersona === null ? <option value="">{user.personaLabel}</option> : null}
              {personas.map((persona) => (
                <option key={persona.email} value={personaKeyFor(persona.email)}>
                  {persona.personaLabel}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        <button
          type="button"
          className="app-topbar-icon app-topbar-search-toggle"
          aria-label="Search work orders, units and vendors"
          aria-expanded={searchOpen}
          aria-controls="app-record-search"
          onClick={() => setSearchOpen((open) => !open)}
        >
          <NavIcon name="search" />
        </button>

        <NotificationBell key={user.userId} />

        <button
          type="button"
          className="app-topbar-icon"
          title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          onClick={toggle}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
          </svg>
        </button>
        </div>
      </header>

      <AppWorkspace key={user.userId} roles={roles} can={can} pathname={location.pathname} viewerKey={user.userId}
        counts={navCounts}>
        {children}
      </AppWorkspace>
      <CompactNavigation
        primary={compactTabs}
        all={visibleTabs}
        counts={navCounts}
        pathname={location.pathname}
        theme={theme}
        onToggleTheme={toggle}
      />
      {/* TRBUD-459: "Who did this PM?" after a PM reaches a completed stage. */}
      <PmCreditPromptHost />
      </SignOutProvider>
    </SessionContext.Provider>
  );
}

/**
 * "Previewing as mechanic. Exit preview." — TRBUD-253.
 *
 * Rendered above the nav on EVERY screen while a preview is in force, and it is
 * not dismissible. A preview narrows what the API will allow, so somebody who has
 * forgotten they are in one reads every later refusal as a defect — which is the
 * failure mode the banner exists to prevent, and a banner you can hide does not
 * prevent it.
 *
 * The exit control posts `{ role: null }` and is never disabled by a capability:
 * clearing a preview is permitted for every account by construction (the
 * database's check runs only when a role is NAMED), so there is no state in which
 * a preview can strand the person who entered it. That is the cold-start rule
 * applied to a feature that could easily have broken it — a narrowed session must
 * always be able to widen back to itself.
 */
function PreviewBanner({ previewRole }: { previewRole: UserRole | null }) {
  const setPreview = useSetPreviewRole();
  if (!previewRole) return null;
  return (
    <div className="preview-banner" role="status">
      <span>
        Previewing as <strong>{previewRole}</strong>.
      </span>
      <button
        type="button"
        className="btn btn-secondary"
        disabled={setPreview.isPending}
        onClick={() =>
          setPreview.mutate({ role: null, idempotencyKey: newIdempotencyKey("exit-preview") })
        }
      >
        {setPreview.isPending ? "Exiting…" : "Exit preview"}
      </button>
      {setPreview.error ? (
        <span className="text-muted">Could not exit the preview. Try again.</span>
      ) : null}
    </div>
  );
}

/**
 * The phone's bottom bar and More sheet — TRBUD-439, design v6 frames E and F.
 *
 * The bar: four destinations from the role's compact priorities plus More, each
 * an icon over its short label, 64px, always visible; the active slot carries
 * the accent label and a 3px top edge. The sheet: the account at its head, then
 * the phone-capable pages the bar leaves (`all` is already the phone list, so
 * no "Tablet or desktop" dead ends), the three Reports pages as one row of
 * tiles, the theme, and Sign out.
 *
 * Counts (TRBUD-443): a counted page shows its number on its bar slot and its
 * sheet row, and More carries a dot while a page it holds has something
 * waiting. The accent, never red (DOWN) or orange (OVERDUE). Nothing at zero,
 * before the counts load, or after a failed read.
 */
function CompactNavigation({
  primary,
  all,
  counts,
  pathname,
  theme,
  onToggleTheme,
}: {
  primary: NavigationItem[];
  all: NavigationItem[];
  counts: NavCounts | null;
  pathname: string;
  theme: Theme;
  onToggleTheme: () => void;
}) {
  const requestSignOut = useSignOut();
  const [open, setOpen] = useState(false);
  const moreButton = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const primaryPaths = new Set(primary.map((item) => item.path));
  const extras = all.filter((item) => !primaryPaths.has(item.path));
  const reportSearch = useLocation().search;
  const reports = extras.filter((item) => item.group === "reports");
  const firstReport = reports[0]?.path;
  const moreActive = extras.some((item) => isNavigationPathActive(item, pathname));
  const moreWaiting = extras.some((item) => navCountFor(item.path, counts) !== null);

  useEffect(() => {
    if (!open) return;
    menu.current?.querySelector<HTMLElement>("a, button")?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        moreButton.current?.focus();
      }
      if (event.key === "Tab") {
        const controls = menu.current?.querySelectorAll<HTMLElement>(FOCUSABLE);
        const first = controls?.[0];
        const last = controls?.[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  const close = () => { setOpen(false); moreButton.current?.focus(); };
  const current = (item: NavigationItem) => (isNavigationPathActive(item, pathname) ? "page" : undefined);

  return (
    <>
      {open ? <button type="button" className="app-more-backdrop" aria-label="Close more menu" onClick={close} /> : null}
      {open ? (
        <div id="compact-more" className="app-more-sheet" ref={menu} role="dialog" aria-modal="true" aria-label="More navigation">
          <AccountSummary />
          {extras.map((item) => {
            if (item.group === "reports") {
              // Reports is one labelled row of three tiles, so its pages cost one
              // row of the sheet, not three (design v2 #12, v4 #1).
              if (item.path !== firstReport) return null;
              return (
                <div key="reports" className="app-sheet-reports-group">
                  <h2 className="app-sheet-label" id="app-sheet-reports-label">
                    <NavIcon name="reports" />
                    Reports <span className="app-sheet-label-note">· charts only on a phone</span>
                  </h2>
                  <div className="app-sheet-reports" role="group" aria-labelledby="app-sheet-reports-label">
                    {reports.map((report) => (
                      <Link key={report.path} to={pathname.startsWith("/reports") ? `${report.path}${reportSearch}` : report.path} aria-current={current(report)} onClick={close}>
                        <NavIcon name={iconForPath(report.path)} />
                        <span>{report.label}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              );
            }
            const count = navCountFor(item.path, counts);
            return (
              <Link key={item.path} to={item.path} aria-current={current(item)} onClick={close}
                aria-label={count === null ? undefined : navCountName(item.label, count)}>
                <NavIcon name={iconForPath(item.path)} />
                <span>{item.label}</span>
                {count !== null ? (
                  <span className="app-nav-count app-sheet-count" aria-hidden="true">{navCountText(count)}</span>
                ) : null}
              </Link>
            );
          })}
          <button type="button" onClick={onToggleTheme}>
            <NavIcon name="moon" />
            {theme === "dark" ? "Use light theme" : "Use dark theme"}
          </button>
          <button
            type="button"
            onClick={requestSignOut}
          >
            <NavIcon name="sign-out" />
            Sign out
          </button>
        </div>
      ) : null}
      <nav className="app-bottombar" aria-label="Primary navigation">
        {primary.map((item) => {
          const count = navCountFor(item.path, counts);
          return (
            <Link
              key={item.path}
              to={item.path}
              aria-current={current(item)}
              aria-label={count === null ? undefined : navCountName(item.shortLabel, count)}
            >
              <NavIcon name={iconForPath(item.path)} />
              <span className="app-bottombar-label">{item.shortLabel}</span>
              {count !== null ? (
                <span className="app-nav-count app-bottombar-count" aria-hidden="true">{navCountText(count)}</span>
              ) : null}
            </Link>
          );
        })}
        <button
          ref={moreButton}
          type="button"
          className={moreActive ? "app-bottombar-active" : undefined}
          aria-expanded={open}
          aria-controls="compact-more"
          aria-label={moreWaiting ? "More, items waiting" : undefined}
          onClick={() => setOpen((current) => !current)}
        >
          <NavIcon name="more" />
          <span className="app-bottombar-label">More</span>
          {moreWaiting ? <span className="app-nav-dot app-bottombar-dot" aria-hidden="true" /> : null}
        </button>
      </nav>
    </>
  );
}

/**
 * The persona key the API expects, derived from the email.
 *
 * The API owns the mapping (`PERSONAS` in `apps/api/src/session.ts`); this reverses
 * it from the local part rather than duplicating the table, so adding a persona
 * server-side needs no client change.
 */
function personaKeyFor(email: string): string {
  return email.split("@")[0] ?? email;
}

/**
 * The h3 + scope tag every screen opens with, and an OPTIONAL one-line description.
 *
 * `description` used to be required, and the requirement is what produced the
 * clutter it was removed for: a screen with nothing to add still had to say
 * something, so nine of the seventeen ended up restating their own title
 * ("Users — who can sign in, and what they may do once they have") or repeating
 * a note that already appears further down the same page. The Work-order queue's
 * read "overdue first, then priority and age" directly above a footnote reading
 * "Sorted by overdue first, then priority and age".
 *
 * So it is optional now, and the bar for passing one is that it tells the reader
 * something the title and the screen do not. Restating the title is not.
 *
 * **And the bar was raised again on 8 September 2026: ONE SHORT SENTENCE.** Eden:
 * *"get the wall of text off of the subheader section of the pages it looks
 * unprofessional."* Four of the survivors had grown into paragraphs — Labour rates
 * carried four sentences, Vendor match review two long ones — each individually
 * justifiable and collectively a block of grey text between every title and the
 * thing the screen is for.
 *
 * The rule that replaced "is it worth saying?" is **"is it worth saying HERE?"**.
 * A caveat that changes what somebody types belongs beside the box they type it
 * into; a caveat about how to read a number belongs beside the number. Neither
 * was deleted — Labour rates' "not a wage, and saving never re-prices posted work"
 * now sits under the rate field, and Vendor match review's "a ruling does not
 * merge or move any data" sits above the pairs being ruled on. Both are read at
 * the moment they matter instead of skimmed past on arrival, which is the same
 * reasoning `ScopeNote` follows in `primitives.tsx`.
 */
export function ScreenHeader({
  title,
  description,
  aside,
}: {
  title: string;
  description?: string | undefined;
  meta?: ScopeMarker | undefined;
  aside?: ReactNode;
}) {
  const inRouter = useInRouterContext();
  /*
   * The page head (TRBUD-439; design v1 (c), (f) "Page header"): the eyebrow
   * names the page's menu group, the title is `--tb-title`, the description is
   * one sentence of lede, and the screen's own controls sit at the right. Every
   * screen gets it from here, so no screen changes.
   */
  return (
    <header className="screen-header page-head">
      <div className="page-head-text">
      {inRouter ? <PageEyebrow /> : null}
      {/*
        `h4` at `h3`'s size — TRBUD-242, and the level is the fix while the
        class is what makes it cost nothing.

        Lighthouse's `heading-order` (weight 3) failed on TEN of the
        twenty-three screens, always the same way: this header rendered `<h3>`
        and every panel heading beneath it is `<h5>`, so the first section on
        the page skipped a level. Ten screens, one cause.

        Moving the header DOWN to `h4` rather than the panels UP is
        deliberate, and it is the smaller of two true statements:

        * `h4` -> `h5` -> `h6` is now a continuous outline on every screen that
          uses this component, fixed in one place, with `.h-3` holding the type
          at exactly the size it had (see the note beside the scale in
          industry.css).
        * The RIGHT outline starts at `h1` for the screen name. Getting there
          means renumbering roughly forty panel headings across thirty files
          and re-deriving each nesting depth, and a wrong guess trades this
          skip for a different one. That is the joint UI pass DEC-07 defers,
          and it should be done with the design open rather than to satisfy an
          audit -- no Lighthouse audit requires an `h1`.

        So this is not "an h4 because four is a nice number": it is the level
        that makes the existing panel headings correct without touching them.
        If the panels are ever renumbered, this goes to `h1` in the same edit.
      */}
      <h4 className="h-3 page-title">{title}</h4>
      {description ? <p className="page-lede">{description}</p> : null}
      </div>
      {aside ? <div className="page-actions">{aside}</div> : null}
    </header>
  );
}

/**
 * The page head's eyebrow: the label of the menu group the current page belongs
 * to ("Work" on a work order, "Setup" on a Settings sub-page), or nothing on a
 * page no menu entry owns. Read from the same table the menu draws, so the two
 * cannot disagree. Outside a router (a component rendered on its own) there is
 * no page, so no eyebrow.
 */
function PageEyebrow() {
  const { pathname } = useLocation();
  const group = navigationGroupFor(pathname);
  return group ? <span className="eyebrow">{group.label}</span> : null;
}
